import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { FiLock, FiEye, FiEyeOff } from 'react-icons/fi';

const AuthOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, var(--primary-blue) 0%, #1e40af 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  padding: 20px;
`;

const AuthCard = styled(motion.div)`
  background: white;
  border-radius: 16px;
  padding: 40px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  text-align: center;
  width: 100%;
  max-width: 400px;
`;

const AuthIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, var(--primary-orange) 0%, #ea580c 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 24px;
  font-size: 2rem;
  color: white;
`;

const AuthTitle = styled.h2`
  color: var(--primary-blue);
  margin-bottom: 12px;
  font-size: 1.8rem;
  font-weight: 700;
`;

const AuthSubtitle = styled.p`
  color: var(--medium-gray);
  margin-bottom: 32px;
  font-size: 1rem;
  line-height: 1.5;
`;

const PasswordForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const PasswordInputWrapper = styled.div`
  position: relative;
`;

const PasswordInput = styled.input`
  width: 100%;
  padding: 16px 20px;
  padding-right: 50px;
  border: 2px solid #e5e7eb;
  border-radius: 12px;
  font-size: 1rem;
  transition: all 0.3s ease;
  box-sizing: border-box;
  
  &:focus {
    outline: none;
    border-color: var(--primary-orange);
    box-shadow: 0 0 0 3px rgba(249, 115, 22, 0.1);
  }
  
  &.error {
    border-color: #dc2626;
    box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
  }
`;

const PasswordToggle = styled.button`
  position: absolute;
  right: 16px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: var(--medium-gray);
  cursor: pointer;
  padding: 4px;
  
  &:hover {
    color: var(--primary-orange);
  }
`;

const AuthButton = styled(motion.button)`
  background: linear-gradient(135deg, var(--primary-orange) 0%, #ea580c 100%);
  color: white;
  border: none;
  padding: 16px 24px;
  border-radius: 12px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled(motion.div)`
  background: #fee2e2;
  color: #dc2626;
  padding: 12px 16px;
  border-radius: 8px;
  font-size: 0.9rem;
  margin-top: 12px;
  border: 1px solid #fecaca;
`;

const BrandingFooter = styled.div`
  margin-top: 32px;
  padding-top: 24px;
  border-top: 1px solid #e5e7eb;
  color: var(--medium-gray);
  font-size: 0.9rem;
`;

const SITE_PASSWORD = 'preview2024'; // Change this to your desired password

const SiteAuth = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Check if user is already authenticated
    const authStatus = sessionStorage.getItem('siteAuthenticated');
    if (authStatus === 'true') {
      setIsAuthenticated(true);
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Simulate a small delay for better UX
    await new Promise(resolve => setTimeout(resolve, 500));

    if (password === SITE_PASSWORD) {
      sessionStorage.setItem('siteAuthenticated', 'true');
      setIsAuthenticated(true);
    } else {
      setError('Incorrect password. Please try again.');
      setPassword('');
    }
    
    setIsLoading(false);
  };

  if (isAuthenticated) {
    return children;
  }

  return (
    <AuthOverlay>
      <AuthCard
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.3 }}
      >
        <AuthIcon>
          <FiLock />
        </AuthIcon>
        
        <AuthTitle>Ahaar Sewa</AuthTitle>
        <AuthSubtitle>
          This site is currently in preview mode.<br />
          Please enter the password to continue.
        </AuthSubtitle>

        <PasswordForm onSubmit={handleSubmit}>
          <PasswordInputWrapper>
            <PasswordInput
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter site password"
              className={error ? 'error' : ''}
              disabled={isLoading}
              autoFocus
            />
            <PasswordToggle
              type="button"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <FiEyeOff /> : <FiEye />}
            </PasswordToggle>
          </PasswordInputWrapper>

          <AuthButton
            type="submit"
            disabled={!password || isLoading}
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            {isLoading ? 'Authenticating...' : 'Enter Site'}
          </AuthButton>
        </PasswordForm>

        {error && (
          <ErrorMessage
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
          >
            {error}
          </ErrorMessage>
        )}

        <BrandingFooter>
          <strong>Feeding Communities, One Meal at a Time</strong>
        </BrandingFooter>
      </AuthCard>
    </AuthOverlay>
  );
};

export default SiteAuth; 